import { setupWorker } from 'msw/browser';
import type { HttpHandler } from 'msw';
import { createHandlers } from './handlers';
import { validateConfig, type MSWConfig } from './utils/config-loader';

/**
 * MSW browser worker setup
 */

export interface SetupMSWOptions {
  /** BFF gateway base URL */
  baseUrl: string;
  /** MSW configuration object (if provided, configPath is ignored) */
  config: MSWConfig;
  /** Custom override handlers */
  overrideHandlers?: HttpHandler[];
  /** Whether to enable quiet mode (less console logging) */
  quiet?: boolean;
}

/**
 * Setup MSW for browser mocking
 *
 * @param options - Setup options
 * @returns Promise that resolves when MSW is ready
 */
export async function setupMSW(options: SetupMSWOptions): Promise<void> {
  try {
    // Create handlers based on configuration
    const handlers = createHandlers(
      options.baseUrl,
      validateConfig(options.config),
      options.overrideHandlers,
    );

    if (handlers.length === 0) {
      console.log('[MSW] No handlers enabled');
      return;
    }

    // Setup worker
    const worker = setupWorker(...handlers);

    // Start worker
    await worker.start({
      onUnhandledRequest: 'bypass', // Don't warn about unhandled requests
      quiet: options.quiet ?? false,
    });

    console.log('[MSW] Mocking enabled with', handlers.length, 'handlers');
    logEnabledEndpoints(options.config);
  } catch (error) {
    console.error('[MSW] Failed to setup:', error);
  }
}

/**
 * Log enabled endpoints for debugging
 */
function logEnabledEndpoints(config: MSWConfig): void {
  if (!config.endpoints) {
    console.log('[MSW] Using preset:', config.preset);
    return;
  }

  const enabled = Object.entries(config.endpoints)
    .filter(([, value]) => value === true)
    .map(([key]) => key);

  if (enabled.length > 0) {
    console.log('[MSW] Enabled endpoints:', enabled.join(', '));
  }
}

// Re-export types and utilities
export type { MSWConfig } from './utils/config-loader';
export { isEndpointEnabled } from './utils/config-loader';
export { createHandlers } from './handlers';
export * from './factories';
